import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import { feedbackService } from '../utils/api';

const FEEDBACK_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'quality', label: 'Quality' },
  { value: 'accuracy', label: 'Accuracy' },
  { value: 'improvement', label: 'Improvement' }
];

const FeedbackForm = ({ 
  open, 
  onClose, 
  callId, 
  feedbackToEdit = null, 
  onFeedbackSubmitted 
}) => {
  const [formData, setFormData] = useState({
    feedback_text: '',
    rating: null,
    feedback_type: 'general'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (feedbackToEdit) {
      setFormData({
        feedback_text: feedbackToEdit.feedback_text || '',
        rating: feedbackToEdit.rating || null,
        feedback_type: feedbackToEdit.feedback_type || 'general'
      });
    } else {
      setFormData({
        feedback_text: '',
        rating: null,
        feedback_type: 'general'
      });
    }
  }, [feedbackToEdit, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.feedback_text.trim()) {
      toast.error('Please enter feedback text');
      return;
    }

    setLoading(true);
    try {
      let result;
      if (feedbackToEdit) {
        // Update existing feedback
        result = await feedbackService.updateFeedback(feedbackToEdit.id, formData);
        toast.success('Feedback updated successfully!');
      } else {
        // Create new feedback
        result = await feedbackService.createFeedback(callId, formData);
        toast.success('Feedback submitted successfully!');
      }
      
      onFeedbackSubmitted(result);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {feedbackToEdit ? 'Edit Feedback' : 'Add Feedback'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* Feedback Type */}
          <FormControl fullWidth>
            <InputLabel>Feedback Type</InputLabel>
            <Select
              value={formData.feedback_type}
              label="Feedback Type"
              onChange={(e) => handleInputChange('feedback_type', e.target.value)}
              disabled={loading}
            >
              {FEEDBACK_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Rating */}
          <Box>
            <Typography component="legend" sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
              Rating (Optional)
            </Typography>
            <Rating
              value={formData.rating}
              onChange={(event, newValue) => handleInputChange('rating', newValue)}
              disabled={loading}
              size="large"
            />
          </Box>

          {/* Feedback Text */}
          <TextField
            label="Feedback"
            multiline
            rows={4}
            value={formData.feedback_text}
            onChange={(e) => handleInputChange('feedback_text', e.target.value)}
            placeholder="Enter your feedback about this call..."
            disabled={loading}
            fullWidth
            required
            inputProps={{ maxLength: 2000 }}
            helperText={`${formData.feedback_text.length}/2000 characters`}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.feedback_text.trim()}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Submitting...' : (feedbackToEdit ? 'Update' : 'Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackForm; 